import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FloorPlanUpload } from "@/components/FloorPlanUpload";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import StudioShell from "@/components/StudioShell";
import { RoomPicker } from "@/components/RoomPicker";
import { ArrowRight, ArrowLeft, Upload, X, Sparkles } from "lucide-react";

const DISCIPLINES = [
  { id: "Interior", title: "Interior Design Project", blurb: "Spaces inside an existing or planned building — residences, offices, hospitality, retail. Mood boards per room, materials, furniture and lighting." },
  { id: "Architecture", title: "Architecture Project", blurb: "A new building or major extension — massing, façade, site and landscape plus the key interior spaces. Mood boards for exterior and principal volumes." },
];
const TYPES = {
  Interior: ["Residence","Apartment","Penthouse","Villa","Farmhouse","Office","Co-working","Restaurant","Café","Hotel","Resort","Retail","Showroom","Clinic","Salon / Spa","Other"],
  Architecture: ["Independent House","Villa","Farmhouse","Apartment Building","Row Housing","Office Building","Commercial Complex","Mixed-use","Hotel / Resort","Institutional","Industrial","Interior Fit-out Shell","Other"],
};
const SCOPES = {
  Interior: ["Full interior","Renovation","Furnishing & styling","Turnkey"],
  Architecture: ["New build","Extension","Renovation & façade","Master plan"],
};
const SITE_CONTEXT = ["Urban","Suburban","Rural","Coastal","Hillside","Gated community"];
const AREA_UNITS = ["sq ft","sq m","sq yd","acres"];
const BUDGETS = ["Essential","Mid-range","Premium","Luxury"];

const Q_SECTIONS = [
  ["personality","Personality","How would you describe your client's personality?",["Minimal","Warm","Sophisticated","Bold","Traditional","Contemporary","Artistic","Nature-oriented","Luxury-oriented","Experimental"]],
  ["feeling","Space Feeling","How should the space feel?",["Calm","Warm","Elegant","Luxurious","Cozy","Dramatic","Minimal","Earthy","Bright","Moody","Timeless","Playful"]],
  ["materials","Material Preferences","",["Natural stone","Marble","Wood","Veneer","Metal","Glass","Concrete","Terrazzo","Fabric","Leather","Fluted surfaces","Textured plaster","Wallpaper"]],
  ["furniture","Furniture Character","",["Clean & minimal","Soft & curved","Sculptural","Classic","Contemporary","Traditional","Statement pieces"]],
  ["lighting","Lighting","",["Bright natural","Soft warm","Dramatic","Layered","Minimal","Decorative"]],
];
const Q_SECTIONS_ARCH = [
  ["personality","Client Character","How would you describe the client?",["Minimal","Warm","Sophisticated","Bold","Traditional","Contemporary","Artistic","Nature-oriented","Luxury-oriented","Experimental"]],
  ["feeling","Spatial Experience","How should the building feel to move through?",["Calm","Grounded","Monumental","Light-filled","Intimate","Dramatic","Timeless","Sculptural","Porous","Sheltering"]],
  ["massing","Massing & Form","",["Compact & cubic","Linear & low","Stacked volumes","Courtyard-centred","Cantilevered","Fragmented pavilions","Split-level","Tower & podium"]],
  ["facade","Façade & Envelope","",["Exposed brick","Stone cladding","Timber screens","Lime plaster / render","Glass & steel","Board-formed concrete","Metal cladding","Louvred / perforated skin","Deep verandahs"]],
  ["roof","Roof Form","",["Flat","Pitched / gabled","Butterfly","Vaulted","Green roof","Overhanging eaves","Sawtooth / clerestory"]],
  ["site_priorities","Site Response","",["Views","Privacy","Daylight orientation","Cross ventilation","Indoor–outdoor flow","Courtyard / garden","Vastu compliance","Vehicle access / parking","Level change"]],
  ["sustainability","Sustainability","",["Passive cooling","Rainwater harvesting","Solar ready","Local materials","Thermal mass","Low-VOC finishes","Net-zero ambition"]],
];
const PALETTES = ["Neutral","Warm","Cool","Earthy","Monochromatic","Colourful"];
const TERMS = {
  Interior: { step1: "Interior Project Details", step2: "Client Questionnaire", spaces: "Rooms / Spaces Required", spacesHint: "Tap a room to add it; tap again or use +/− for multiples (Bedroom 01, 02…). Rename or remove any space in the list.", refs: "What interiors, architecture or spaces inspire the client?", start: "Start AI analysis" },
  Architecture: { step1: "Architecture Project Details", step2: "Client Brief & Site", spaces: "Building Programme (spaces & exterior elements)", spacesHint: "Add façade, entrance, courtyard and landscape elements as well as the principal interior volumes. Use +/− for repeated units (Bedroom 01, 02…).", refs: "Which buildings, architects or places inspire the client? Note site constraints (orientation, neighbours, slope, setbacks).", start: "Start concept analysis" },
};

function Chip({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`px-4 py-2 text-sm border transition-colors ${active ? "bg-black text-white border-black" : "bg-white border-stone-300 hover:border-stone-500"}`}>
      {children}
    </button>
  );
}

export default function ProjectWizard() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const [step, setStep] = useState(editId ? 1 : 0);
  const [plan, setPlan] = useState(null);
  const [pendingPlan, setPendingPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [projectId, setProjectId] = useState(editId || null);
  const [details, setDetails] = useState({ name:"", client_name:"", discipline:"Interior", project_type:"Residence", scope:"Full interior", location:"", area:"", area_unit:"sq ft", plot_area:"", floors:1, site_context:"Urban", budget:"Mid-range", rooms: ["Living Room","Master Bedroom","Kitchen"] });
  const pickDiscipline = (d) => setDetails(prev => ({ ...prev, discipline: d, project_type: TYPES[d][0], scope: SCOPES[d][0], rooms: d === "Architecture" ? ["Front Façade","Entrance","Living Room","Master Bedroom"] : ["Living Room","Master Bedroom","Kitchen"] }));
  const [q, setQ] = useState({ personality:[], feeling:[], materials:[], furniture:[], lighting:[], massing:[], facade:[], roof:[], site_priorities:[], sustainability:[], palette:"Warm", colors_love:[], colors_dislike:[], inspiration_notes:"", lifestyle:{ occupants:"", pets:"", work_from_home:false, entertaining:"" } });
  const [colorLove, setColorLove] = useState("");
  const [colorHate, setColorHate] = useState("");
  const [images, setImages] = useState([]);

  useEffect(() => {
    if (!editId) return;
    api.get(`/projects/${editId}`).then(r => {
      const p = r.data;
      setDetails(d => ({ ...d, ...Object.fromEntries(Object.keys(d).map(k => [k, p[k] ?? d[k]])) }));
      if (p.questionnaire) setQ(prev => ({ ...prev, ...p.questionnaire, lifestyle: { ...prev.lifestyle, ...(p.questionnaire.lifestyle || {}) } }));
      setImages((p.images || []).map(i => ({ ...i, data_url: i.data_url?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${i.data_url}` : i.data_url })));
      setPlan(p.floor_plan || null);
    }).catch(e => toast.error(apiError(e, "Could not load project")));
  }, [editId]);

  const T = TERMS[details.discipline] || TERMS.Interior;
  const sections = details.discipline === "Architecture" ? Q_SECTIONS_ARCH : Q_SECTIONS;
  const toggle = (section, val) => setQ(prev => { const cur = prev[section] || []; return { ...prev, [section]: cur.includes(val) ? cur.filter(x=>x!==val) : [...cur, val] }; });

  const step1Valid = details.name && details.client_name && details.rooms.length > 0;

  const saveDetails = async () => {
    if (!step1Valid) { toast.error("Project name, client name and at least one space are required"); return; }
    setSaving(true);
    try {
      const body = { ...details, area: String(details.area || ""), plot_area: String(details.plot_area || ""), floors: Number(details.floors) || 1 };
      const res = projectId ? await api.put(`/projects/${projectId}`, body) : await api.post("/projects", body);
      const pid = res.data.id;
      setProjectId(pid);
      if (pendingPlan) {
        const r = await api.post(`/projects/${pid}/floor-plan`, { data_url: pendingPlan.data_url, filename: pendingPlan.filename });
        setPlan(r.data); setPendingPlan(null);
      } else if (plan === null && editId) {
        await api.delete(`/projects/${pid}/floor-plan`).catch(() => {});
      }
      setStep(2);
      toast.success(projectId ? "Project details updated" : "Project created");
    } catch (e) { toast.error(apiError(e, "Failed to save project")); }
    setSaving(false);
  };

  const saveQuestionnaire = async () => {
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/questionnaire`, q);
      setStep(3);
    } catch (e) { toast.error("Failed to save questionnaire"); }
    setSaving(false);
  };

  const handleFiles = async (files) => {
    for (const f of Array.from(files)) {
      const data_url = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); });
      try {
        const resp = await api.post(`/projects/${projectId}/images`, { data_url, note: "" });
        setImages(prev => [...prev, { id: resp.data.id, data_url, note: "" }]);
      } catch { toast.error("Image upload failed"); }
    }
  };

  const updateNote = async (id, note) => {
    setImages(prev => prev.map(i => i.id === id ? { ...i, note } : i));
    try { await api.patch(`/projects/${projectId}/images/${id}`, { note }); } catch (e) { toast.error(apiError(e, "Could not save note")); }
  };

  const removeImage = async (id) => {
    setImages(prev => prev.filter(i => i.id !== id));
    try { await api.delete(`/projects/${projectId}/images/${id}`); } catch (e) { toast.error(apiError(e, "Could not remove image")); }
  };

  const finishAndAnalyse = () => {
    navigate(`/project/${projectId}/analysis?start=1`);
  };

  return (
    <StudioShell>
      <div className="px-6 sm:px-12 py-10 max-w-[1200px] mx-auto">
        <div className="eyebrow mb-3">{editId ? "Edit Project" : "New Project"} · Step {String(step+1).padStart(2,'0')} / 04</div>
        <h1 className="editorial-heading text-4xl sm:text-5xl mb-2">
          {step===0 && "What are we designing?"}
          {step===1 && T.step1}
          {step===2 && T.step2}
          {step===3 && "Upload Inspiration"}
        </h1>
        <div className="flex gap-2 mt-6 mb-10">
          {[0,1,2,3].map(n => <div key={n} className="h-[2px] flex-1" style={{ background: n<=step ? "#1A1A18" : "#E5E5DF" }} />)}
        </div>

        {step === 0 && (
          <div data-testid="wizard-step-0" className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {DISCIPLINES.map(d => (
              <button key={d.id} type="button" onClick={() => pickDiscipline(d.id)} data-testid={`discipline-${d.id}`}
                className={`studio-card text-left p-8 sm:p-10 transition-colors ${details.discipline === d.id ? "outline outline-2 outline-black" : "hover:border-stone-400"}`}>
                <div className="eyebrow mb-3">{d.id}</div>
                <div className="font-serif-editorial text-3xl mb-3">{d.title}</div>
                <p className="text-sm text-stone-600 leading-relaxed">{d.blurb}</p>
              </button>
            ))}
            <div className="md:col-span-2 flex justify-end">
              <button onClick={() => setStep(1)} data-testid="wizard-step0-next" className="inline-flex items-center gap-2 px-6 py-3 bg-black text-white text-sm">
                Continue as {details.discipline} <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div data-testid="wizard-step-1" className="studio-card p-8 sm:p-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Field label="Project Name" testid="input-project-name">
                <input value={details.name} onChange={e=>setDetails({...details, name:e.target.value})} className="w-full border-b border-stone-300 py-2 bg-transparent outline-none focus:border-black" placeholder="Villa Calvi" data-testid="input-project-name" />
              </Field>
              <Field label="Client Name" testid="input-client-name">
                <input value={details.client_name} onChange={e=>setDetails({...details, client_name:e.target.value})} className="w-full border-b border-stone-300 py-2 bg-transparent outline-none focus:border-black" placeholder="Henderson Residence" data-testid="input-client-name" />
              </Field>
              <Field label={details.discipline === "Architecture" ? "Building Type" : "Type of Building / Space"}>
                <select value={details.project_type} onChange={e=>setDetails({...details, project_type:e.target.value})} className="w-full border-b border-stone-300 py-2 bg-transparent outline-none focus:border-black" data-testid="select-project-type">
                  {TYPES[details.discipline].map(t=><option key={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Scope of Work">
                <div className="flex gap-2 mt-2 flex-wrap">
                  {SCOPES[details.discipline].map(sc => <Chip key={sc} active={details.scope===sc} onClick={()=>setDetails({...details, scope:sc})} testid={`scope-${sc}`}>{sc}</Chip>)}
                </div>
              </Field>
              <Field label="Location">
                <input value={details.location} onChange={e=>setDetails({...details, location:e.target.value})} className="w-full border-b border-stone-300 py-2 bg-transparent outline-none focus:border-black" placeholder="Milano" data-testid="input-location" />
              </Field>
              <Field label={details.discipline === "Architecture" ? "Built-up Area" : "Carpet / Built-up Area"}>
                <div className="flex gap-3">
                  <input type="number" min="0" value={details.area} onChange={e=>setDetails({...details, area:e.target.value})} className="flex-1 border-b border-stone-300 py-2 bg-transparent outline-none focus:border-black" placeholder="3200" data-testid="input-area" />
                  <select value={details.area_unit} onChange={e=>setDetails({...details, area_unit:e.target.value})} className="border-b border-stone-300 py-2 bg-transparent outline-none" data-testid="select-area-unit">
                    {AREA_UNITS.map(u=><option key={u}>{u}</option>)}
                  </select>
                </div>
              </Field>
              {details.discipline === "Architecture" && (
                <Field label="Plot / Site Area">
                  <input type="number" min="0" value={details.plot_area} onChange={e=>setDetails({...details, plot_area:e.target.value})} className="w-full border-b border-stone-300 py-2 bg-transparent outline-none focus:border-black" placeholder={`6000 ${details.area_unit}`} data-testid="input-plot-area" />
                </Field>
              )}
              <Field label="Number of Floors">
                <div className="flex items-center gap-3 mt-1">
                  <button type="button" onClick={()=>setDetails({...details, floors: Math.max(1, (details.floors||1)-1)})} className="w-9 h-9 border border-stone-300 hover:border-black" data-testid="floors-dec">−</button>
                  <div className="font-serif-editorial text-2xl w-24 text-center" data-testid="floors-value">{details.discipline === "Architecture" ? (details.floors === 1 ? "Ground" : `G + ${details.floors-1}`) : `${details.floors} ${details.floors===1?"floor":"floors"}`}</div>
                  <button type="button" onClick={()=>setDetails({...details, floors: Math.min(60, (details.floors||1)+1)})} className="w-9 h-9 border border-stone-300 hover:border-black" data-testid="floors-inc">+</button>
                </div>
              </Field>
              {details.discipline === "Architecture" && (
                <Field label="Site Context">
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {SITE_CONTEXT.map(sc => <Chip key={sc} active={details.site_context===sc} onClick={()=>setDetails({...details, site_context:sc})} testid={`site-${sc}`}>{sc}</Chip>)}
                  </div>
                </Field>
              )}
              <Field label="Design Budget">
                <div className="flex gap-2 mt-2 flex-wrap">
                  {BUDGETS.map(b => <Chip key={b} active={details.budget===b} onClick={()=>setDetails({...details, budget:b})} testid={`budget-${b}`}>{b}</Chip>)}
                </div>
              </Field>
              <div className="md:col-span-2">
                <Field label="Floor Plan / Site Drawing (optional)">
                  <div className="mt-2">
                    <FloorPlanUpload plan={plan} pending={pendingPlan} discipline={details.discipline} onPick={setPendingPlan} onRemove={() => { setPendingPlan(null); setPlan(null); }} />
                  </div>
                </Field>
                <Field label={T.spaces} hint={T.spacesHint}>
                  <div className="mt-3">
                    <RoomPicker rooms={details.rooms} onChange={(rooms) => setDetails({ ...details, rooms })} discipline={details.discipline} />
                  </div>
                </Field>
              </div>
            </div>
            <div className="flex justify-between mt-10">
              <button onClick={() => setStep(0)} data-testid="wizard-step1-back" className="inline-flex items-center gap-2 px-6 py-3 border border-stone-300 text-sm"><ArrowLeft size={16} /> Back</button>
              <button onClick={saveDetails} disabled={saving||!step1Valid} data-testid="wizard-step1-next" className="inline-flex items-center gap-2 px-6 py-3 bg-black text-white text-sm disabled:opacity-40">
                Continue <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="wizard-step-2" className="space-y-10">
            {sections.map(([key, title, sub, options]) => (
              <div key={key} className="studio-card p-8">
                <div className="eyebrow mb-2">{title}</div>
                {sub && <p className="text-sm text-stone-500 mb-4">{sub}</p>}
                <div className="flex flex-wrap gap-2">
                  {options.map(o => <Chip key={o} active={(q[key]||[]).includes(o)} onClick={()=>toggle(key,o)} testid={`q-${key}-${o}`}>{o}</Chip>)}
                </div>
              </div>
            ))}
            <div className="studio-card p-8">
              <div className="eyebrow mb-4">Colour Preferences</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="text-sm mb-2">Overall palette</div>
                  <div className="flex flex-wrap gap-2">{PALETTES.map(p => <Chip key={p} active={q.palette===p} onClick={()=>setQ({...q, palette:p})} testid={`palette-${p}`}>{p}</Chip>)}</div>
                </div>
                <div>
                  <div className="text-sm mb-2">Colours you love</div>
                  <div className="flex gap-2">
                    <input value={colorLove} onChange={e=>setColorLove(e.target.value)} className="flex-1 border-b border-stone-300 py-1 outline-none bg-transparent" placeholder="e.g. Warm ivory" />
                    <button onClick={()=>{ if(colorLove){ setQ({...q, colors_love:[...q.colors_love, colorLove]}); setColorLove(""); }}} className="text-sm px-3 border border-stone-300">+</button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">{q.colors_love.map((c)=><span key={c} className="text-xs px-2 py-1 bg-stone-100">{c}</span>)}</div>
                  <div className="text-sm mt-4 mb-2">Colours you dislike</div>
                  <div className="flex gap-2">
                    <input value={colorHate} onChange={e=>setColorHate(e.target.value)} className="flex-1 border-b border-stone-300 py-1 outline-none bg-transparent" placeholder="e.g. Cold blue-grey" />
                    <button onClick={()=>{ if(colorHate){ setQ({...q, colors_dislike:[...q.colors_dislike, colorHate]}); setColorHate(""); }}} className="text-sm px-3 border border-stone-300">+</button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">{q.colors_dislike.map((c)=><span key={c} className="text-xs px-2 py-1 bg-stone-100">{c}</span>)}</div>
                </div>
              </div>
            </div>
            <div className="studio-card p-8">
              <div className="eyebrow mb-4">Lifestyle</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                <label className="block">Occupants<input value={q.lifestyle.occupants} onChange={e=>setQ({...q, lifestyle:{...q.lifestyle, occupants:e.target.value}})} className="w-full border-b border-stone-300 py-1 outline-none bg-transparent" data-testid="q-lifestyle-occupants" /></label>
                <label className="block">Pets<input value={q.lifestyle.pets} onChange={e=>setQ({...q, lifestyle:{...q.lifestyle, pets:e.target.value}})} className="w-full border-b border-stone-300 py-1 outline-none bg-transparent" data-testid="q-lifestyle-pets" /></label>
                <label className="block">Entertaining style<input value={q.lifestyle.entertaining} onChange={e=>setQ({...q, lifestyle:{...q.lifestyle, entertaining:e.target.value}})} className="w-full border-b border-stone-300 py-1 outline-none bg-transparent" data-testid="q-lifestyle-entertaining" /></label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={q.lifestyle.work_from_home} onChange={e=>setQ({...q, lifestyle:{...q.lifestyle, work_from_home:e.target.checked}})} data-testid="q-lifestyle-wfh" /> Works from home</label>
              </div>
            </div>
            <div className="studio-card p-8">
              <div className="eyebrow mb-2">Design References</div>
              <p className="text-sm text-stone-500 mb-4">{T.refs}</p>
              <textarea value={q.inspiration_notes} onChange={e=>setQ({...q, inspiration_notes:e.target.value})} rows={4} className="w-full border border-stone-300 p-3 outline-none bg-white text-sm" placeholder="Axel Vervoordt, Vincent Van Duysen, Amber Interiors…" data-testid="q-inspiration" />
            </div>
            <div className="flex justify-between">
              <button onClick={()=>setStep(1)} className="inline-flex items-center gap-2 px-6 py-3 border border-stone-300 text-sm" data-testid="wizard-step2-back"><ArrowLeft size={16}/> Back</button>
              <button onClick={saveQuestionnaire} disabled={saving} className="inline-flex items-center gap-2 px-6 py-3 bg-black text-white text-sm" data-testid="wizard-step2-next">Continue <ArrowRight size={16} /></button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="wizard-step-3" className="space-y-6">
            <div className="studio-card p-10">
              <div className="eyebrow mb-2">Upload Your Inspiration</div>
              <p className="text-sm text-stone-500 mb-6">Upload photographs that represent spaces, furniture, colours, materials or details you love. Add a note to guide the AI.</p>
              <label htmlFor="file-input" data-testid="image-upload-dropzone" className="block border-2 border-dashed border-stone-300 py-16 text-center cursor-pointer hover:border-stone-500 transition">
                <Upload size={28} strokeWidth={1.2} className="mx-auto mb-4 text-stone-400" />
                <div className="font-serif-editorial text-xl">Drop images or click to upload</div>
                <div className="text-xs text-stone-500 mt-2">JPG · JPEG · PNG · WEBP</div>
                <input id="file-input" type="file" accept="image/*" multiple className="hidden" onChange={e=>handleFiles(e.target.files)} data-testid="image-file-input" />
              </label>
            </div>
            {images.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {images.map(img => (
                  <div key={img.id} className="studio-card overflow-hidden">
                    <div className="aspect-[4/3] bg-stone-100 overflow-hidden"><img src={img.data_url} alt="" className="w-full h-full object-cover" /></div>
                    <div className="p-4">
                      <input value={img.note} onChange={e=>updateNote(img.id, e.target.value)} placeholder="e.g. Love the lighting" className="w-full text-sm border-b border-stone-200 py-1 outline-none bg-transparent" data-testid={`img-note-${img.id}`} />
                      <button onClick={()=>removeImage(img.id)} className="text-xs text-stone-400 mt-3 hover:text-black flex items-center gap-1" data-testid={`img-remove-${img.id}`}><X size={12}/> Remove</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-between pt-4">
              <button onClick={()=>setStep(2)} className="inline-flex items-center gap-2 px-6 py-3 border border-stone-300 text-sm" data-testid="wizard-step3-back"><ArrowLeft size={16}/> Back</button>
              <div className="flex gap-3">
                <button onClick={() => navigate(`/project/${projectId}/analysis`)} className="inline-flex items-center gap-2 px-6 py-3 border border-stone-300 text-sm hover:border-black" data-testid="wizard-review-btn">Review before analysis</button>
                <button onClick={finishAndAnalyse} className="inline-flex items-center gap-2 px-6 py-3 bg-black text-white text-sm" data-testid="wizard-analyse-btn">
                  <Sparkles size={16} /> {T.start}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </StudioShell>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="block">
      <div className="eyebrow mb-2">{label}</div>
      {hint && <div className="text-xs text-stone-500 -mt-1 mb-1">{hint}</div>}
      {children}
    </div>
  );
}
